import Phaser from 'phaser';
import { isCalmMode } from '../audio/CalmMode.js';

/** Chain level color progression */
const CHAIN_COLORS = [
  '#ffdd00', // level 1 - yellow
  '#ffaa00', // level 2 - orange
  '#ff6600', // level 3 - deep orange
  '#ff2200', // level 4 - red
  '#ff00ff', // level 5+ - purple/gold
];

const CHAIN_FONT_SIZES = [16, 20, 24, 28, 34];

/** Visual juice effects manager */
export class JuiceEffects {
  private scene: Phaser.Scene;

  /** Highest chain level seen on the current combo's announcer. A drop
   *  (chainLevel <= this) means the chain broke and a new combo started,
   *  so the announce-shake gate resets. */
  private lastAnnounceChain: number = 0;
  /** Announcement tiers (keyed by minLevel) that have already shaken in
   *  the current combo. Each tier shakes only on its first appearance —
   *  a second NICE! in the same combo shows text but doesn't re-shake. */
  private announcedShakeTiers: Set<number> = new Set();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Coin pickup with chain-scaled feedback.
   *
   *  `stakeMultiplier` (default 1) multiplies the displayed number. The
   *  underlying snake coin count stays raw — chest thresholds compute
   *  off raw values, so this is purely cosmetic. A player who paid x10
   *  sees "+10" instead of "+1" on every coin pickup. */
  coinPickup(
    worldX: number,
    worldY: number,
    effectiveValue: number,
    chainLevel: number,
    stakeMultiplier: number = 1
  ): void {
    const colorIdx = Math.min(chainLevel - 1, CHAIN_COLORS.length - 1);
    const color = CHAIN_COLORS[Math.max(0, colorIdx)];
    const sizeIdx = Math.min(chainLevel - 1, CHAIN_FONT_SIZES.length - 1);
    const fontSize = CHAIN_FONT_SIZES[Math.max(0, sizeIdx)];

    // Floating number — displayed value scales with stake.
    const displayValue = effectiveValue * stakeMultiplier;
    const prefix = chainLevel >= 2 ? `x${chainLevel} ` : '';
    const text = this.scene.add.text(worldX, worldY - 8, `${prefix}+${displayValue}`, {
      fontFamily: 'monospace',
      fontSize: `${fontSize}px`,
      color,
      stroke: '#000',
      strokeThickness: Math.min(2 + chainLevel, 5),
      fontStyle: chainLevel >= 4 ? 'bold' : 'normal',
    }).setOrigin(0.5).setDepth(150);

    // Scale punch
    const startScale = chainLevel >= 3 ? 1.5 : 1.2;
    text.setScale(startScale);

    this.scene.tweens.add({
      targets: text,
      y: worldY - 30 - chainLevel * 5,
      alpha: 0,
      scale: chainLevel >= 3 ? 0.8 : 0.6,
      duration: 500 + chainLevel * 100,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });

    // No screen shake here: a plain coin pickup (chain 1) isn't a combo,
    // and shaking on every coin was the "shakes when you grab the first
    // coin" noise. The shake now lives on the multiplier (chainUpdate).

    // Chain level 3+: screen edge pulse
    if (chainLevel >= 3) {
      this.screenEdgePulse(color);
    }

    // Chain level 5: bigger flash
    if (chainLevel >= 5) {
      this.scene.cameras.main.flash(100, 255, 200, 0, false);
    }
  }

  /** Chain level update indicator */
  chainUpdate(chainLevel: number, multiplier: number): void {
    const { width } = this.scene.scale;
    const colorIdx = Math.min(chainLevel - 1, CHAIN_COLORS.length - 1);
    const color = CHAIN_COLORS[Math.max(0, colorIdx)];

    const text = this.scene.add.text(width / 2, 80, `CHAIN x${chainLevel}  ${multiplier.toFixed(2)}x`, {
      fontFamily: 'monospace',
      fontSize: '18px',
      color,
      stroke: '#000',
      strokeThickness: 3,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(200).setAlpha(0);

    this.scene.tweens.add({
      targets: text,
      alpha: 1,
      scale: 1.2,
      duration: 150,
      yoyo: true,
      hold: 400,
      onComplete: () => text.destroy(),
    });

    // Shake lives on the multiplier, not on coin pickup. The combo starts
    // at chain 2 (×1.5) — strong punch there, softening as the multiplier
    // climbs so the action stays readable. chain 1 is a plain coin: no shake.
    if (chainLevel >= 2) {
      const intensity = Math.max(0.0015, 0.006 - (chainLevel - 2) * 0.0015);
      const duration = Math.max(40, 90 - (chainLevel - 2) * 15);
      this.scene.cameras.main.shake(duration, intensity);
    }
  }

  /** Kill effect: hit-freeze + time dilation + screen shake */
  killEffect(worldX: number, worldY: number, victimName: string): void {
    // Screen shake (heavy)
    this.scene.cameras.main.shake(200, 0.01);

    // Brief flash
    this.scene.cameras.main.flash(80, 255, 50, 50, false);

    // "KILL" text
    const killText = this.scene.add.text(worldX, worldY - 20, 'KILL!', {
      fontFamily: 'monospace',
      fontSize: '28px',
      color: '#ff4444',
      stroke: '#000',
      strokeThickness: 4,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(160).setScale(0.3);

    this.scene.tweens.add({
      targets: killText,
      scale: 1.3,
      duration: 150,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: killText,
          alpha: 0,
          y: worldY - 50,
          duration: 600,
          onComplete: () => killText.destroy(),
        });
      },
    });

    // Hit-freeze effect
    this.scene.time.timeScale = 0.3;
    this.scene.time.delayedCall(80, () => { this.scene.time.timeScale = 1; });

    // Victim explosion particles
    if (!this.scene.textures.exists('death_pixel')) {
      const g = this.scene.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 4, 4);
      g.generateTexture('death_pixel', 4, 4);
      g.destroy();
    }
    const burst = this.scene.add.particles(worldX, worldY, 'death_pixel', {
      speed: { min: 40, max: 150 },
      angle: { min: 0, max: 360 },
      scale: { start: 1, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 300, max: 800 },
      quantity: 30,
      tint: [0xff4444, 0xff8822, 0xffdd00],
      blendMode: 'ADD',
      emitting: false,
    }).setDepth(160);
    burst.explode(30);
    this.scene.time.delayedCall(900, () => burst.destroy());
  }

  /** Other snake died nearby — visible but smaller explosion */
  otherSnakeDeath(worldX: number, worldY: number): void {
    if (!this.scene.textures.exists('death_pixel')) {
      const g = this.scene.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 4, 4);
      g.generateTexture('death_pixel', 4, 4);
      g.destroy();
    }

    // Body chunks
    const chunks = this.scene.add.particles(worldX, worldY, 'death_pixel', {
      speed: { min: 30, max: 100 },
      angle: { min: 0, max: 360 },
      scale: { start: 1, end: 0 },
      alpha: { start: 0.8, end: 0 },
      lifespan: { min: 300, max: 700 },
      quantity: 20,
      tint: [0xff4444, 0xff6644, 0xcc2222],
      blendMode: 'ADD',
      emitting: false,
    }).setDepth(160);
    chunks.explode(20);

    // Small shockwave ring
    const ring = this.scene.add.circle(worldX, worldY, 2, 0xff4444, 0.4).setDepth(159);
    this.scene.tweens.add({
      targets: ring, radius: 40, alpha: 0, duration: 300, ease: 'Power2',
      onComplete: () => ring.destroy(),
    });

    this.scene.time.delayedCall(800, () => chunks.destroy());
  }

  /** Death effect when player dies — dramatic pixel explosion */
  playerDeath(worldX?: number, worldY?: number): void {
    // Heavy screen shake + double flash (red then white)
    this.scene.cameras.main.shake(500, 0.025);
    this.scene.cameras.main.flash(200, 255, 50, 50, false);
    this.scene.time.delayedCall(200, () => {
      this.scene.cameras.main.flash(150, 255, 255, 255, false);
    });

    // Hit-freeze moment
    this.scene.time.timeScale = 0.15;
    this.scene.time.delayedCall(120, () => { this.scene.time.timeScale = 1; });

    if (worldX !== undefined && worldY !== undefined) {
      // Ensure particle textures exist
      if (!this.scene.textures.exists('death_pixel')) {
        const g = this.scene.add.graphics();
        g.fillStyle(0xffffff, 1);
        g.fillRect(0, 0, 4, 4);
        g.generateTexture('death_pixel', 4, 4);
        g.destroy();
      }
      if (!this.scene.textures.exists('death_chunk')) {
        const g = this.scene.add.graphics();
        g.fillStyle(0xffffff, 1);
        g.fillRect(0, 0, 6, 6);
        g.generateTexture('death_chunk', 6, 6);
        g.destroy();
      }

      // Wave 1: Large body chunks flying outward
      const chunks = this.scene.add.particles(worldX, worldY, 'death_chunk', {
        speed: { min: 60, max: 200 },
        angle: { min: 0, max: 360 },
        scale: { start: 1.5, end: 0.2 },
        alpha: { start: 1, end: 0 },
        lifespan: { min: 600, max: 1400 },
        quantity: 50,
        tint: [0xff3333, 0xff6644, 0xcc1111, 0xff8855],
        rotate: { min: 0, max: 360 },
        blendMode: 'NORMAL',
        emitting: false,
      }).setDepth(200);
      chunks.explode(50);

      // Wave 2: Fine pixel spray (faster, smaller, ADD blend for glow)
      const spray = this.scene.add.particles(worldX, worldY, 'death_pixel', {
        speed: { min: 100, max: 300 },
        angle: { min: 0, max: 360 },
        scale: { start: 1, end: 0 },
        alpha: { start: 0.9, end: 0 },
        lifespan: { min: 300, max: 800 },
        quantity: 60,
        tint: [0xff4444, 0xffaa00, 0xffffff],
        blendMode: 'ADD',
        emitting: false,
      }).setDepth(201);
      spray.explode(60);

      // Wave 3: Coin scatter (golden, delayed slightly)
      this.scene.time.delayedCall(150, () => {
        const coins = this.scene.add.particles(worldX, worldY, 'death_pixel', {
          speed: { min: 40, max: 160 },
          angle: { min: 0, max: 360 },
          scale: { start: 1.2, end: 0.3 },
          alpha: { start: 1, end: 0 },
          lifespan: { min: 600, max: 1500 },
          quantity: 25,
          tint: [0xffd700, 0xffaa00, 0xffe066],
          blendMode: 'ADD',
          emitting: false,
        }).setDepth(199);
        coins.explode(25);
        this.scene.time.delayedCall(1600, () => coins.destroy());
      });

      // Expanding shockwave ring
      const ring = this.scene.add.circle(worldX, worldY, 3, 0xff4444, 0.6).setDepth(198);
      this.scene.tweens.add({
        targets: ring, radius: 80, alpha: 0, duration: 400, ease: 'Power2',
        onComplete: () => ring.destroy(),
      });

      this.scene.time.delayedCall(1500, () => { chunks.destroy(); spray.destroy(); });
    }

    const { width, height } = this.scene.scale;
    const cx = width / 2;
    const cy = height / 2;

    const text = this.scene.add.text(cx, cy, 'DEAD', {
      fontFamily: 'monospace',
      fontSize: '72px',
      color: '#ff2222',
      stroke: '#000',
      strokeThickness: 8,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(300).setScrollFactor(0).setScale(0.1);

    this.scene.tweens.add({
      targets: text,
      scale: 1.4,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          alpha: 0,
          scale: 1.8,
          duration: 1200,
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  /** Extraction effect */
  extractionEffect(coinsBanked: number): void {
    this.scene.cameras.main.flash(200, 0, 255, 100, false);

    const { width, height } = this.scene.scale;

    // "BANKED!" with coin count
    const text = this.scene.add.text(width / 2, height * 0.4, `BANKED!\n+${coinsBanked}`, {
      fontFamily: 'monospace',
      fontSize: '56px',
      color: '#00ff88',
      stroke: '#000',
      strokeThickness: 6,
      align: 'center',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(300).setScrollFactor(0).setScale(0.3);

    this.scene.tweens.add({
      targets: text,
      scale: 1.2,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          alpha: 0,
          y: text.y - 40,
          duration: 1500,
          delay: 500,
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  /** Chain combo announcer — Candy Crush style escalating text.
   *  NICE!/INCREDIBLE! stay subtle so the build-up reads cleanly; the
   *  strong shake is saved as the UNSTOPPABLE! payoff (chain 8+). */
  private static CHAIN_ANNOUNCEMENTS = [
    { minLevel: 3, text: 'NICE!', color: '#ffdd00', fontSize: '28px', shake: 0.003 },
    { minLevel: 5, text: 'INCREDIBLE!', color: '#ff8800', fontSize: '36px', shake: 0.004 },
    { minLevel: 8, text: 'UNSTOPPABLE!', color: '#ff2200', fontSize: '48px', shake: 0.012 },
  ];

  chainAnnounce(chainLevel: number): void {
    if (isCalmMode()) return; // no-stimulus: skip NICE!/INCREDIBLE!/UNSTOPPABLE!
    let announcement = null;
    for (const a of JuiceEffects.CHAIN_ANNOUNCEMENTS) {
      if (chainLevel >= a.minLevel) announcement = a;
    }
    if (!announcement) return;

    const { width } = this.scene.scale;

    // The GameScene camera is at zoom 2x. setScrollFactor(0) doesn't undo
    // the zoom — it pins the object to the camera but world→screen still
    // scales around the camera midpoint. Solve for the world y that maps
    // to a desired screen y just below the chest widget + timer.
    const cam = this.scene.cameras.main;
    const targetScreenY = 130;
    const worldY = (targetScreenY - cam.height / 2) / cam.zoom + cam.height / 2;
    const text = this.scene.add.text(width / 2, worldY, announcement.text, {
      fontFamily: 'monospace',
      fontSize: announcement.fontSize,
      color: announcement.color,
      stroke: '#000000',
      strokeThickness: 5,
      fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(400).setScale(0.1).setAlpha(0);

    // Shake only the FIRST time each tier appears in a combo. chainAnnounce
    // fires on every chain_update ≥3, so NICE! shows at chain 3 AND 4 — we
    // only want one shake per tier. A chainLevel that didn't climb past the
    // last one means the chain broke and a new combo began → reset the gate.
    if (chainLevel <= this.lastAnnounceChain) this.announcedShakeTiers.clear();
    this.lastAnnounceChain = chainLevel;

    if (!this.announcedShakeTiers.has(announcement.minLevel)) {
      this.announcedShakeTiers.add(announcement.minLevel);
      // Longer shake for the UNSTOPPABLE! payoff so it lands as a beat.
      const shakeMs = announcement.minLevel >= 8 ? 280 : 150;
      this.scene.cameras.main.shake(shakeMs, announcement.shake);
    }

    this.scene.tweens.add({
      targets: text,
      scale: 1.0,
      alpha: 1,
      duration: 150,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          alpha: 0,
          scale: 0.8,
          y: text.y - 10,
          duration: 400,
          delay: 300,
          ease: 'Power2',
          onComplete: () => text.destroy(),
        });
      },
    });

    if (chainLevel >= 8) {
      this.scene.cameras.main.flash(100, 255, 150, 0, false);
    }
  }

  /** Near-miss feedback when a good chain breaks */
  chainBreakFeedback(maxChainReached: number): void {
    const { width } = this.scene.scale;

    const text = this.scene.add.text(width / 2, 130, `SO CLOSE! x${maxChainReached}`, {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#888888',
      stroke: '#000000',
      strokeThickness: 4,
      fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(400).setAlpha(0);

    this.scene.tweens.add({
      targets: text,
      alpha: 1,
      duration: 100,
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          x: { from: text.x - 4, to: text.x + 4 },
          duration: 50,
          yoyo: true,
          repeat: 3,
          onComplete: () => {
            this.scene.tweens.add({
              targets: text,
              alpha: 0,
              y: text.y + 10,
              duration: 400,
              delay: 200,
              onComplete: () => text.destroy(),
            });
          },
        });
      },
    });
  }

  /** Screen edge color pulse (for high chains) */
  private screenEdgePulse(color: string): void {
    if (isCalmMode()) return; // no-stimulus: skip the full-screen strobe
    const { width, height } = this.scene.scale;
    const hex = parseInt(color.replace('#', ''), 16);

    const overlay = this.scene.add.rectangle(width / 2, height / 2, width, height, hex, 0.15)
      .setScrollFactor(0).setDepth(99);

    this.scene.tweens.add({
      targets: overlay,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => overlay.destroy(),
    });
  }
}
